import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSession } from '@/domains/auth/services/auth.service';
import {
  fetchAdminStats,
  fetchAllUsers,
  updateUserStatus,
  fetchAllDocuments,
  validateDocument,
  fetchZones,
  createZone,
  updateZone,
  deleteZone,
  fetchCategories,
  createCategory,
  deleteCategory,
  fetchVehicleTypes,
  fetchSpecializations,
  fetchAvailabilitySlots,
  createSpecialization,
  createAvailabilitySlot,
  createVehicleType,
  updateVehicleType,
  deleteVehicleType,
  updateSpecialization,
  deleteSpecialization,
  updateAvailabilitySlot,
  deleteAvailabilitySlot,
  fetchTypes,
  createType,
  updateType,
  deleteType,
  // fetchUserTypes,
  // addUserType,
  // removeUserType,
  // fetchUserPermissions,
  // addUserPermission,
  // removeUserPermission,
} from '@/domains/admin/services/admin.service';
import { handleErrorApi, showSuccess } from '@/utils/handleErrorApi';
import { paginateClient } from '@/utils/pagination';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import {
  PremiumDashboardLayout,
  StatCard,
  EmptyState,
  LoadingSpinner,
} from '@/components/dashboard/PremiumDashboardLayout';
import { SmartDataTable, type Column } from '@/components/ui/SmartDataTable';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import type {
  AdminStats,
  AdminUser,
  AdminDocument,
  ConfigZone,
  ConfigCategory,
  ConfigReference,
  ConfigType,
} from '@/domains/admin/services/admin.service';

// Configuration APIs - should ONLY be used by Super-Admin
const CONFIGURATION_APIS = [
  'fetchZones',
  'createZone',
  'updateZone',
  'deleteZone',
  'fetchCategories',
  'createCategory',
  'deleteCategory',
  'fetchVehicleTypes',
  'fetchSpecializations',
  'fetchAvailabilitySlots',
  'createSpecialization',
  'createAvailabilitySlot',
  'createVehicleType',
  'updateVehicleType',
  'deleteVehicleType',
  'updateSpecialization',
  'deleteSpecialization',
  'updateAvailabilitySlot',
  'deleteAvailabilitySlot',
  'fetchTypes',
  'createType',
  'updateType',
  'deleteType',
  'fetchUserTypes',
  'addUserType',
  'removeUserType',
  'fetchUserPermissions',
  'addUserPermission',
  'removeUserPermission',
] as const;

const TABS = [
  { id: 'accueil', label: 'Tableau de bord', icon: 'ri-dashboard-line' },
  { id: 'utilisateurs', label: 'Utilisateurs', icon: 'ri-user-line' },
  { id: 'documents', label: 'Documents', icon: 'ri-file-shield-line' },
  { id: 'zones', label: 'Zones', icon: 'ri-map-pin-line' },
  { id: 'categories', label: 'Catégories', icon: 'ri-price-tag-3-line' },
  { id: 'types', label: 'Types', icon: 'ri-list-unordered' },
  { id: 'references', label: 'Références', icon: 'ri-settings-3-line' },
];

const PAGE_TITLES: Record<string, string> = {
  accueil: 'Administration ONAPILA',
  utilisateurs: 'Gestion des utilisateurs',
  documents: 'Validation des documents',
  zones: 'Zones géographiques',
  categories: 'Catégories de documents',
  types: 'Types utilisateurs',
  references: 'Données de référence',
};

const USER_STATUS_OPTIONS = ['active', 'pending', 'suspended', 'inactive'] as const;

const TYPE_LABELS: Record<string, string> = {
  client: 'Client',
  community_delivery: 'Livreur communautaire',
  professional_delivery: 'Livreur pro',
  company: 'Entreprise',
  admin: 'Admin',
  super_admin: 'Super admin',
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('accueil');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [userSearch, setUserSearch] = useState('');
  const [userPage, setUserPage] = useState(1);
  const [userPerPage, setUserPerPage] = useState(10);
  const [docSearch, setDocSearch] = useState('');
  const [docPage, setDocPage] = useState(1);
  const [docPerPage, setDocPerPage] = useState(10);

  const [stats, setStats] = useState<AdminStats | null>(null);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [documents, setDocuments] = useState<AdminDocument[]>([]);
  const [zones, setZones] = useState<ConfigZone[]>([]);
  const [categories, setCategories] = useState<ConfigCategory[]>([]);
  const [types, setTypes] = useState<ConfigType[]>([]);
  const [vehicleTypes, setVehicleTypes] = useState<ConfigReference[]>([]);
  const [specializations, setSpecializations] = useState<ConfigReference[]>([]);
  const [availabilitySlots, setAvailabilitySlots] = useState<ConfigReference[]>([]);

  const [newZoneName, setNewZoneName] = useState('');
  const [editingZone, setEditingZone] = useState<{ id: number; name: string } | null>(null);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryType, setNewCategoryType] = useState('document');
  const [newTypeName, setNewTypeName] = useState('');
  const [editingType, setEditingType] = useState<{ id: number; name: string } | null>(null);
  const [newRefName, setNewRefName] = useState('');
  const [userFilter, setUserFilter] = useState('all');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const session = getSession();
      const userRole = session?.user.types?.[0]?.name ?? '';
      const isSuperAdmin = userRole === 'super_admin';

      // Base data - available to both Admin and Super-Admin
      const [statsData, usersData, docsData] = await Promise.all([
        fetchAdminStats(),
        fetchAllUsers(),
        fetchAllDocuments(),
      ]);
      setStats(statsData);
      setUsers(usersData);
      setDocuments(docsData);

      // Configuration data - ONLY for Super-Admin
      if (isSuperAdmin) {
        const [zonesData, catsData, typesData, vTypes, specs, slots] = await Promise.all([
          fetchZones(),
          fetchCategories(),
          fetchTypes(),
          fetchVehicleTypes(),
          fetchSpecializations(),
          fetchAvailabilitySlots(),
        ]);
        setZones(zonesData);
        setCategories(catsData);
        setTypes(typesData);
        setVehicleTypes(vTypes);
        setSpecializations(specs);
        setAvailabilitySlots(slots);
      }
    } catch (err) {
      handleErrorApi(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!getSession()) {
      navigate('/login');
      return;
    }
    loadData();
  }, [navigate, loadData]);

  const handleUserStatus = async (userId: number, status: string) => {
    try {
      await updateUserStatus(userId, status);
      setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, status } : u)));
      showSuccess('Statut utilisateur mis à jour');
    } catch (err) {
      handleErrorApi(err);
    }
  };

  const handleValidateDoc = async (docId: number, status: 'valid' | 'dismiss' | 'suspended') => {
    try {
      const updated = await validateDocument(docId, status);
      setDocuments((prev) => prev.map((d) => (d.id === docId ? updated : d)));
      showSuccess(`Document ${status === 'valid' ? 'validé' : status === 'dismiss' ? 'rejeté' : 'suspendu'}`);
    } catch (err) {
      handleErrorApi(err);
    }
  };

  const handleCreateZone = async () => {
    const session = getSession();
    const userRole = session?.user.types?.[0]?.name ?? '';
    if (userRole !== 'super_admin') {
      handleErrorApi(new Error('Accès refusé: Réservé au Super-Admin'));
      return;
    }
    if (!newZoneName.trim()) return;
    try {
      const zone = await createZone(newZoneName.trim());
      setZones((prev) => [...prev, zone]);
      setNewZoneName('');
      showSuccess('Zone créée');
    } catch (err) {
      handleErrorApi(err);
    }
  };

  const handleUpdateZone = async () => {
    const session = getSession();
    const userRole = session?.user.types?.[0]?.name ?? '';
    if (userRole !== 'super_admin') {
      handleErrorApi(new Error('Accès refusé: Réservé au Super-Admin'));
      return;
    }
    if (!editingZone) return;
    try {
      const updated = await updateZone(editingZone.id, editingZone.name);
      setZones((prev) => prev.map((z) => (z.id === updated.id ? updated : z)));
      setEditingZone(null);
      showSuccess('Zone mise à jour');
    } catch (err) {
      handleErrorApi(err);
    }
  };

  const handleDeleteZone = async (zoneId: number) => {
    const session = getSession();
    const userRole = session?.user.types?.[0]?.name ?? '';
    if (userRole !== 'super_admin') {
      handleErrorApi(new Error('Accès refusé: Réservé au Super-Admin'));
      return;
    }
    try {
      await deleteZone(zoneId);
      setZones((prev) => prev.filter((z) => z.id !== zoneId));
      showSuccess('Zone supprimée');
    } catch (err) {
      handleErrorApi(err);
    }
  };

  const handleCreateCategory = async () => {
    const session = getSession();
    const userRole = session?.user.types?.[0]?.name ?? '';
    if (userRole !== 'super_admin') {
      handleErrorApi(new Error('Accès refusé: Réservé au Super-Admin'));
      return;
    }
    if (!newCategoryName.trim()) return;
    try {
      const cat = await createCategory(newCategoryType, newCategoryName.trim());
      setCategories((prev) => [...prev, cat]);
      setNewCategoryName('');
      showSuccess('Catégorie créée');
    } catch (err) {
      handleErrorApi(err);
    }
  };

  const handleDeleteCategory = async (catId: number) => {
    const session = getSession();
    const userRole = session?.user.types?.[0]?.name ?? '';
    if (userRole !== 'super_admin') {
      handleErrorApi(new Error('Accès refusé: Réservé au Super-Admin'));
      return;
    }
    try {
      await deleteCategory(catId);
      setCategories((prev) => prev.filter((c) => c.id !== catId));
      showSuccess('Catégorie supprimée');
    } catch (err) {
      handleErrorApi(err);
    }
  };

  const handleCreateRef = async (type: 'vehicle' | 'specialization' | 'slot') => {
    const session = getSession();
    const userRole = session?.user.types?.[0]?.name ?? '';
    if (userRole !== 'super_admin') {
      handleErrorApi(new Error('Accès refusé: Réservé au Super-Admin'));
      return;
    }
    if (!newRefName.trim()) return;
    try {
      if (type === 'vehicle') {
        const item = await createVehicleType(newRefName.trim());
        setVehicleTypes((prev) => [...prev, item]);
      } else if (type === 'specialization') {
        const item = await createSpecialization(newRefName.trim());
        setSpecializations((prev) => [...prev, item]);
      } else {
        const item = await createAvailabilitySlot(newRefName.trim());
        setAvailabilitySlots((prev) => [...prev, item]);
      }
      setNewRefName('');
      showSuccess('Référence ajoutée');
    } catch (err) {
      handleErrorApi(err);
    }
  };

  const handleCreateType = async () => {
    const session = getSession();
    const userRole = session?.user.types?.[0]?.name ?? '';
    if (userRole !== 'super_admin') {
      handleErrorApi(new Error('Accès refusé: Réservé au Super-Admin'));
      return;
    }
    if (!newTypeName.trim()) return;
    try {
      const type = await createType(newTypeName.trim());
      setTypes((prev) => [...prev, type]);
      setNewTypeName('');
      showSuccess('Type créé');
    } catch (err) {
      handleErrorApi(err);
    }
  };

  const handleUpdateType = async () => {
    const session = getSession();
    const userRole = session?.user.types?.[0]?.name ?? '';
    if (userRole !== 'super_admin') {
      handleErrorApi(new Error('Accès refusé: Réservé au Super-Admin'));
      return;
    }
    if (!editingType) return;
    try {
      const updated = await updateType(editingType.id, editingType.name);
      setTypes((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
      setEditingType(null);
      showSuccess('Type mis à jour');
    } catch (err) {
      handleErrorApi(err);
    }
  };

  const handleDeleteType = async (typeId: number) => {
    const session = getSession();
    const userRole = session?.user.types?.[0]?.name ?? '';
    if (userRole !== 'super_admin') {
      handleErrorApi(new Error('Accès refusé: Réservé au Super-Admin'));
      return;
    }
    try {
      await deleteType(typeId);
      setTypes((prev) => prev.filter((t) => t.id !== typeId));
      showSuccess('Type supprimé');
    } catch (err) {
      handleErrorApi(err);
    }
  };

  const session = getSession();
  const userRole = session?.user.types?.[0]?.name ?? '';
  const isSuperAdmin = userRole === 'super_admin';
  const isAdmin = userRole === 'admin';

  const pendingUsers = users.filter((u) => u.status === 'pending');
  const pendingDocs = documents.filter((d) => d.status === 'pending');
  const filteredUsers = userFilter === 'all' ? users : users.filter((u) => u.status === userFilter);

  // Filter tabs based on user role
  const filteredTabs = TABS.filter((t) => {
    if (isSuperAdmin) return true; // Super-Admin sees all tabs (accueil, utilisateurs, documents, zones, categories, types, references)
    if (isAdmin) {
      // Admin only sees: accueil, utilisateurs, documents, commandes
      return ['accueil', 'utilisateurs', 'documents', 'commandes'].includes(t.id);
    }
    return false; // Other roles see nothing
  });

  const userPaginated = paginateClient(
    filteredUsers,
    userPage,
    userPerPage,
    userSearch,
    (u, q) =>
      `${u.firstnames} ${u.lastname}`.toLowerCase().includes(q) ||
      (u.email ?? '').toLowerCase().includes(q) ||
      u.phone.includes(q),
  );

  const docPaginated = paginateClient(
    documents,
    docPage,
    docPerPage,
    docSearch,
    (d, q) =>
      (d.category?.name ?? '').toLowerCase().includes(q) ||
      (d.user ? `${d.user.firstnames} ${d.user.lastname}` : '').toLowerCase().includes(q) ||
      d.file_url.toLowerCase().includes(q),
  );

  const userColumns: Column<AdminUser>[] = [
    { key: 'name', label: 'Nom', render: (u) => <span className="font-semibold">{u.firstnames} {u.lastname}</span> },
    { key: 'phone', label: 'Téléphone', render: (u) => <span className="text-xs">{u.phone}<br />{u.email ?? '—'}</span> },
    { key: 'type', label: 'Type', render: (u) => TYPE_LABELS[u.types?.[0]?.name ?? ''] ?? '—' },
    { key: 'zone', label: 'Zone', render: (u) => u.zone?.name ?? '—' },
    { key: 'status', label: 'Statut', render: (u) => <StatusBadge status={u.status} /> },
    {
      key: 'actions',
      label: 'Actions',
      render: (u) => (
        <select
          value={u.status}
          onChange={(e) => handleUserStatus(u.id, e.target.value)}
          className="text-xs border border-slate-200 rounded-lg px-2 py-1 bg-white"
        >
          {USER_STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      ),
    },
  ];

  const docColumns: Column<AdminDocument>[] = [
    { key: 'category', label: 'Document', render: (d) => d.category?.name ?? '—' },
    { key: 'user', label: 'Utilisateur', render: (d) => d.user ? `${d.user.firstnames} ${d.user.lastname}` : `#${d.user_id}` },
    { key: 'file', label: 'Fichier', render: (d) => <span className="text-xs truncate max-w-[120px] block">{d.file_url}</span> },
    { key: 'status', label: 'Statut', render: (d) => <StatusBadge status={d.status} /> },
    {
      key: 'actions',
      label: 'Actions',
      render: (d) => d.status === 'pending' ? (
        <div className="flex gap-1">
          <button onClick={() => handleValidateDoc(d.id, 'valid')} className="px-2 py-1 bg-emerald-600 text-white text-[10px] font-bold rounded-lg">OK</button>
          <button onClick={() => handleValidateDoc(d.id, 'dismiss')} className="px-2 py-1 bg-rose-600 text-white text-[10px] font-bold rounded-lg">Non</button>
        </div>
      ) : null,
    },
  ];

  return (
    <PremiumDashboardLayout
      tabs={filteredTabs.map((t) => {
        if (t.id === 'utilisateurs') return { ...t, badge: pendingUsers.length };
        if (t.id === 'documents') return { ...t, badge: pendingDocs.length };
        return t;
      })}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      pageTitle={PAGE_TITLES[activeTab] ?? 'Administration'}
      roleLabel={isSuperAdmin ? 'Super-Admin' : isAdmin ? 'Admin' : 'Back-Office'}
      accent="indigo"
      userName={session ? `${session.user.firstnames} ${session.user.lastname}` : undefined}
      userInitial={session?.user.firstnames?.[0]}
      mobileNavTabIds={isAdmin ? ['accueil', 'utilisateurs', 'documents', 'commandes'] : ['accueil', 'utilisateurs', 'documents', 'zones']}
    >
      {error && <ErrorAlert error={error} onClose={() => setError('')} />}

      {activeTab === 'accueil' && (
        <div className="space-y-6">
          {loading ? (
            <LoadingSpinner />
          ) : stats && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard icon="ri-user-line" label="Utilisateurs" value={stats.total_users} color="indigo" />
                <StatCard icon="ri-time-line" label="En attente" value={stats.pending_users} color="amber" />
                <StatCard icon="ri-truck-line" label="Livreurs actifs" value={stats.active_drivers} color="blue" />
                <StatCard icon="ri-map-pin-line" label="Zones" value={stats.total_zones} color="teal" />
              </div>

              <div className="grid lg:grid-cols-2 gap-6">
                <section className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
                  <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-3">
                    Comptes en attente de validation
                  </h3>
                  {pendingUsers.length === 0 ? (
                    <p className="text-sm text-slate-500">Aucun compte en attente</p>
                  ) : (
                    <div className="space-y-2">
                      {pendingUsers.slice(0, 5).map((u) => (
                        <div key={u.id} className="flex items-center justify-between p-3 bg-amber-50 rounded-xl text-sm">
                          <div>
                            <p className="font-semibold text-slate-800">{u.firstnames} {u.lastname}</p>
                            <p className="text-xs text-slate-500">{TYPE_LABELS[u.types?.[0]?.name ?? ''] ?? u.types?.[0]?.name}</p>
                          </div>
                          <button
                            onClick={() => handleUserStatus(u.id, 'active')}
                            className="px-3 py-1.5 bg-emerald-600 text-white text-xs font-bold rounded-lg"
                          >
                            Valider
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </section>

                <section className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
                  <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-3">
                    Documents à valider
                  </h3>
                  {pendingDocs.length === 0 ? (
                    <p className="text-sm text-slate-500">Aucun document en attente</p>
                  ) : (
                    <div className="space-y-2">
                      {pendingDocs.slice(0, 5).map((d) => (
                        <div key={d.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl text-sm">
                          <div>
                            <p className="font-semibold text-slate-800">{d.category?.name}</p>
                            <p className="text-xs text-slate-500">
                              {d.user ? `${d.user.firstnames} ${d.user.lastname}` : `User #${d.user_id}`}
                            </p>
                          </div>
                          <button
                            onClick={() => handleValidateDoc(d.id, 'valid')}
                            className="px-3 py-1.5 bg-indigo-600 text-white text-xs font-bold rounded-lg"
                          >
                            Valider
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              </div>
            </>
          )}
        </div>
      )}

      {activeTab === 'utilisateurs' && (
        <div className="space-y-4">
          <SearchableSelect
            label="Filtrer par statut"
            options={[
              { value: 'all', label: 'Tous les statuts' },
              ...USER_STATUS_OPTIONS.map((s) => ({ value: s, label: s })),
            ]}
            value={userFilter}
            onChange={(v) => { setUserFilter(String(v)); setUserPage(1); }}
            className="max-w-xs"
          />
          <SmartDataTable
            title="Utilisateurs"
            columns={userColumns}
            data={userPaginated.data}
            rowKey={(u) => u.id}
            meta={userPaginated.meta}
            loading={loading}
            searchValue={userSearch}
            onSearchChange={(v) => { setUserSearch(v); setUserPage(1); }}
            onPageChange={setUserPage}
            onPerPageChange={(n) => { setUserPerPage(n); setUserPage(1); }}
            emptyMessage="Aucun utilisateur"
          />
        </div>
      )}

      {activeTab === 'documents' && (
        <SmartDataTable
          title="Documents à valider"
          columns={docColumns}
          data={docPaginated.data}
          rowKey={(d) => d.id}
          meta={docPaginated.meta}
          loading={loading}
          searchValue={docSearch}
          onSearchChange={(v) => { setDocSearch(v); setDocPage(1); }}
          onPageChange={setDocPage}
          onPerPageChange={(n) => { setDocPerPage(n); setDocPage(1); }}
          emptyMessage="Aucun document"
        />
      )}

      {activeTab === 'zones' && (
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm flex gap-2">
            <input
              value={newZoneName}
              onChange={(e) => setNewZoneName(e.target.value)}
              placeholder="Nom de la nouvelle zone"
              className="flex-1 px-3 py-2 border border-slate-200 rounded-xl text-sm"
            />
            <button onClick={handleCreateZone} className="px-4 py-2 bg-indigo-600 text-white text-xs font-bold rounded-xl">
              Ajouter
            </button>
          </div>

          {loading ? (
            <LoadingSpinner />
          ) : zones.length === 0 ? (
            <EmptyState icon="ri-map-pin-line" message="Aucune zone configurée" />
          ) : (
            <div className="space-y-2">
              {zones.map((z) => (
                <div key={z.id} className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm flex items-center justify-between">
                  {editingZone?.id === z.id ? (
                    <div className="flex gap-2 flex-1">
                      <input
                        value={editingZone.name}
                        onChange={(e) => setEditingZone({ ...editingZone, name: e.target.value })}
                        className="flex-1 px-3 py-1.5 border border-slate-200 rounded-lg text-sm"
                      />
                      <button onClick={handleUpdateZone} className="px-3 py-1.5 bg-indigo-600 text-white text-xs font-bold rounded-lg">OK</button>
                      <button onClick={() => setEditingZone(null)} className="px-3 py-1.5 bg-slate-200 text-xs font-bold rounded-lg">Annuler</button>
                    </div>
                  ) : (
                    <>
                      <span className="text-sm font-semibold text-slate-800">{z.name}</span>
                      <div className="flex gap-2">
                        <button onClick={() => setEditingZone({ id: z.id, name: z.name })} className="p-2 text-slate-400 hover:text-indigo-600">
                          <i className="ri-edit-line" />
                        </button>
                        <button onClick={() => handleDeleteZone(z.id)} className="p-2 text-slate-400 hover:text-rose-600">
                          <i className="ri-delete-bin-line" />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'categories' && (
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm flex flex-col sm:flex-row gap-2">
            <SearchableSelect
              options={[
                { value: 'document', label: 'Document' },
                { value: 'vehicle', label: 'Véhicule' },
                { value: 'other', label: 'Autre' },
              ]}
              value={newCategoryType}
              onChange={(v) => setNewCategoryType(String(v))}
              className="sm:w-48"
            />
            <input
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              placeholder="Nom de la catégorie"
              className="flex-1 px-3 py-2 border border-slate-200 rounded-xl text-sm"
            />
            <button onClick={handleCreateCategory} className="px-4 py-2 bg-indigo-600 text-white text-xs font-bold rounded-xl">
              Ajouter
            </button>
          </div>

          {loading ? (
            <LoadingSpinner />
          ) : categories.length === 0 ? (
            <EmptyState icon="ri-price-tag-3-line" message="Aucune catégorie" />
          ) : (
            <div className="space-y-2">
              {categories.map((c) => (
                <div key={c.id} className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm flex items-center justify-between">
                  <div>
                    <span className="text-sm font-semibold text-slate-800">{c.name}</span>
                    <span className="ml-2 text-[10px] text-slate-400 uppercase">{c.type}</span>
                  </div>
                  <button onClick={() => handleDeleteCategory(c.id)} className="p-2 text-slate-400 hover:text-rose-600">
                    <i className="ri-delete-bin-line" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'types' && (
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm flex gap-2">
            <input
              value={newTypeName}
              onChange={(e) => setNewTypeName(e.target.value)}
              placeholder="Nom du nouveau type"
              className="flex-1 px-3 py-2 border border-slate-200 rounded-xl text-sm"
            />
            <button onClick={handleCreateType} className="px-4 py-2 bg-indigo-600 text-white text-xs font-bold rounded-xl">
              Ajouter
            </button>
          </div>

          {loading ? (
            <LoadingSpinner />
          ) : types.length === 0 ? (
            <EmptyState icon="ri-list-check-line" message="Aucun type configuré" />
          ) : (
            <div className="space-y-2">
              {types.map((t) => (
                <div key={t.id} className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm flex items-center justify-between">
                  {editingType?.id === t.id ? (
                    <div className="flex gap-2 flex-1">
                      <input
                        value={editingType.name}
                        onChange={(e) => setEditingType({ ...editingType, name: e.target.value })}
                        className="flex-1 px-3 py-1.5 border border-slate-200 rounded-lg text-sm"
                      />
                      <button onClick={handleUpdateType} className="px-3 py-1.5 bg-indigo-600 text-white text-xs font-bold rounded-lg">OK</button>
                      <button onClick={() => setEditingType(null)} className="px-3 py-1.5 bg-slate-200 text-xs font-bold rounded-lg">Annuler</button>
                    </div>
                  ) : (
                    <>
                      <span className="text-sm font-semibold text-slate-800">{t.name}</span>
                      <div className="flex gap-2">
                        {/* <button onClick={() => setEditingType({ id: t.id, name: t.name })} className="p-2 text-slate-400 hover:text-indigo-600">
                          <i className="ri-edit-line" />
                        </button>
                        <button onClick={() => handleDeleteType(t.id)} className="p-2 text-slate-400 hover:text-rose-600">
                          <i className="ri-delete-bin-line" />
                        </button> */}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'references' && (
        <div className="space-y-6">
          <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm flex gap-2">
            <input
              value={newRefName}
              onChange={(e) => setNewRefName(e.target.value)}
              placeholder="Nom de la référence"
              className="flex-1 px-3 py-2 border border-slate-200 rounded-xl text-sm"
            />
          </div>

          <EnhancedReferenceSection
            title="Types de véhicule"
            items={vehicleTypes}
            onAdd={() => handleCreateRef('vehicle')}
            onUpdate={async (id, name) => {
              const session = getSession();
              const userRole = session?.user.types?.[0]?.name ?? '';
              if (userRole !== 'super_admin') {
                handleErrorApi(new Error('Accès refusé: Réservé au Super-Admin'));
                return;
              }
              try {
                const updated = await updateVehicleType(id, name);
                setVehicleTypes((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
                showSuccess('Type de véhicule mis à jour');
              } catch (err) {
                handleErrorApi(err);
              }
            }}
            onDelete={async (id) => {
              const session = getSession();
              const userRole = session?.user.types?.[0]?.name ?? '';
              if (userRole !== 'super_admin') {
                handleErrorApi(new Error('Accès refusé: Réservé au Super-Admin'));
                return;
              }
              try {
                await deleteVehicleType(id);
                setVehicleTypes((prev) => prev.filter((t) => t.id !== id));
                showSuccess('Type de véhicule supprimé');
              } catch (err) {
                handleErrorApi(err);
              }
            }}
            loading={loading}
          />
          <EnhancedReferenceSection
            title="Spécialisations"
            items={specializations}
            onAdd={() => handleCreateRef('specialization')}
            onUpdate={async (id, name) => {
              const session = getSession();
              const userRole = session?.user.types?.[0]?.name ?? '';
              if (userRole !== 'super_admin') {
                handleErrorApi(new Error('Accès refusé: Réservé au Super-Admin'));
                return;
              }
              try {
                const updated = await updateSpecialization(id, name);
                setSpecializations((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
                showSuccess('Spécialisation mise à jour');
              } catch (err) {
                handleErrorApi(err);
              }
            }}
            onDelete={async (id) => {
              const session = getSession();
              const userRole = session?.user.types?.[0]?.name ?? '';
              if (userRole !== 'super_admin') {
                handleErrorApi(new Error('Accès refusé: Réservé au Super-Admin'));
                return;
              }
              try {
                await deleteSpecialization(id);
                setSpecializations((prev) => prev.filter((t) => t.id !== id));
                showSuccess('Spécialisation supprimée');
              } catch (err) {
                handleErrorApi(err);
              }
            }}
            loading={loading}
          />
          <EnhancedReferenceSection
            title="Créneaux de disponibilité"
            items={availabilitySlots}
            onAdd={() => handleCreateRef('slot')}
            onUpdate={async (id, name) => {
              const session = getSession();
              const userRole = session?.user.types?.[0]?.name ?? '';
              if (userRole !== 'super_admin') {
                handleErrorApi(new Error('Accès refusé: Réservé au Super-Admin'));
                return;
              }
              try {
                const updated = await updateAvailabilitySlot(id, name);
                setAvailabilitySlots((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
                showSuccess('Créneau mis à jour');
              } catch (err) {
                handleErrorApi(err);
              }
            }}
            onDelete={async (id) => {
              const session = getSession();
              const userRole = session?.user.types?.[0]?.name ?? '';
              if (userRole !== 'super_admin') {
                handleErrorApi(new Error('Accès refusé: Réservé au Super-Admin'));
                return;
              }
              try {
                await deleteAvailabilitySlot(id);
                setAvailabilitySlots((prev) => prev.filter((t) => t.id !== id));
                showSuccess('Créneau supprimé');
              } catch (err) {
                handleErrorApi(err);
              }
            }}
            loading={loading}
          />
        </div>
      )}
    </PremiumDashboardLayout>
  );
}

function EnhancedReferenceSection({
  title,
  items,
  onAdd,
  onUpdate,
  onDelete,
  loading,
}: {
  title: string;
  items: ConfigReference[];
  onAdd: () => void;
  onUpdate: (id: number, name: string) => void;
  onDelete: (id: number) => void;
  loading: boolean;
}) {
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState('');

  const handleEdit = (item: ConfigReference) => {
    setEditingId(item.id);
    setEditingName(item.name);
  };

  const handleSave = () => {
    if (editingId !== null && editingName.trim()) {
      onUpdate(editingId, editingName.trim());
      setEditingId(null);
      setEditingName('');
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditingName('');
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-bold text-slate-800">{title}</h3>
        <button onClick={onAdd} className="px-3 py-1.5 bg-indigo-600 text-white text-xs font-bold rounded-lg">
          <i className="ri-add-line mr-1" />Ajouter
        </button>
      </div>
      {loading ? (
        <LoadingSpinner />
      ) : items.length === 0 ? (
        <p className="text-sm text-slate-500">Aucune entrée</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {items.map((item) => (
            editingId === item.id ? (
              <div key={item.id} className="flex gap-1">
                <input
                  value={editingName}
                  onChange={(e) => setEditingName(e.target.value)}
                  className="px-2 py-1 border border-slate-200 rounded-lg text-xs w-32"
                />
                <button onClick={handleSave} className="px-2 py-1 bg-emerald-600 text-white text-xs font-bold rounded-lg">OK</button>
                <button onClick={handleCancel} className="px-2 py-1 bg-slate-200 text-xs font-bold rounded-lg">X</button>
              </div>
            ) : (
              <div key={item.id} className="flex items-center gap-1">
                <span className="px-3 py-1.5 bg-indigo-50 text-indigo-700 rounded-xl text-xs font-semibold">
                  {item.name}
                </span>
                <button onClick={() => handleEdit(item)} className="p-1 text-slate-400 hover:text-indigo-600">
                  <i className="ri-edit-line text-xs" />
                </button>
                <button onClick={() => onDelete(item.id)} className="p-1 text-slate-400 hover:text-rose-600">
                  <i className="ri-delete-bin-line text-xs" />
                </button>
              </div>
            )
          ))}
        </div>
      )}
    </div>
  );
}
